import { UploadFileTypeEnum } from 'api-hooks/upload/upload.model';
import { DialogProvider } from 'hooks/use-dialog';
import * as React from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import MainFilePicker from './main-file-picker';
import PreviewPicker from './preview-picker';
import { MainPreviewContainer, PickerContainer } from './styles';
import { ReadOnlyText } from '..';
import CustomFormControl from '../custom-form-control';
import { BaseElementInputProps } from '../field-type';
import { FormContext } from '../form';

export interface SingleFilePickerProps extends BaseElementInputProps {
  type: 'file';
  label?: string;
  required?: boolean;
  previewName: string;
  disabled?: boolean;
  maxWidth?: string;
  resizeImage?: boolean;
  resizeWidth?: number;
  resizeHeight?: number;
  fieldType?: 'control' | 'uncontrol';
  fileType: UploadFileTypeEnum;
}

export default function SingleFilePicker(props: SingleFilePickerProps) {
  const { control } = useFormContext<any>();
  const {
    maxWidth,
    resizeHeight,
    resizeWidth,
    resizeImage,
    required,
    fileType,
  } = props;
  const watchValue = useWatch({ control, name: props.name });
  const watchPreviewValue = useWatch({ control, name: props.name });
  const { field, fieldState } = useController({ name: props.name });
  const { field: previewField } = useController({
    name: props.name,
  });
  const context = React.useContext(FormContext);
  const disabled = props.disabled || !context.editable;

  if (!context.editable && !watchValue && !watchPreviewValue) {
    return <ReadOnlyText value="-" label={props.label} />;
  }

  const _onPicked = (files: { filename: string; fileUrl: string }[]) => {
    const file = files[0];
    field.onChange(file.filename);
    previewField.onChange(file.fileUrl);
  };
  const hasValue = !!field.value && !!previewField.value;

  return (
    <DialogProvider>
      <CustomFormControl
        required={required}
        label={props.label}
        disabled={disabled}
        error={fieldState.error?.message}
      >
        <PickerContainer $maxWidth={maxWidth}>
          {hasValue && (
            <MainPreviewContainer>
              <PreviewPicker
                disabled={disabled}
                onDelete={() => {
                  field.onChange(undefined);
                  previewField.onChange(undefined);
                }}
                preview={previewField.value}
                onPicked={_onPicked}
                resizeHeight={resizeHeight}
                resizeImage={resizeImage}
                resizeWidth={resizeWidth}
                fileType={fileType}
              />
            </MainPreviewContainer>
          )}
          {!hasValue && (
            <MainFilePicker
              isError={!disabled && !!fieldState.error?.message}
              disabled={disabled}
              onPicked={_onPicked}
              resizeHeight={resizeHeight}
              resizeImage={resizeImage}
              resizeWidth={resizeWidth}
              fileType={fileType}
            />
          )}
        </PickerContainer>
      </CustomFormControl>
    </DialogProvider>
  );
}
